/*
 * Website Analytics › Overview — the dashboard (spec §3). Reading order:
 * how much → where from → what they looked at → where they are → what it turned into.
 * Every tile loads on its own (progressive), every delta is real or absent.
 */
import React from "react";
import { Sparkline, AreaChart, HBars, Delta } from "./charts";
import { num, usd, deltaOf } from "../sales/territory-common";
import PostalBubbleMap from "../market-intelligence/postal-bubble-map";
import GeoChoropleth from "../../components/geo-choropleth";
import { Card, Pending } from "../../components/analytics-kit";
import { getDashboard, getWindow, getSiteKpis, getPages, getProducts, getUtm, getLocations, getPlaces, getConversions, getFunnels, getFunnelResults, getSeries, getChannels, getDevices, safe, previousPreset, channelsFromUtm, channelsFromServer, seriesRows, seriesCoverage, dailyRows, siteKpiSummary, fmtDuration, pctOf } from "./data";
export default class Overview extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dash: undefined,
      prev: undefined,
      series: undefined,
      prevSeries: undefined,
      channels: undefined,
      devices: undefined,
      win: undefined,
      kpis: undefined,
      pages: undefined,
      products: undefined,
      utm: undefined,
      locations: undefined,
      places: undefined,
      conversions: undefined,
      funnel: undefined,
      mapMode: "us",
      metric: "views"
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
  }
  componentDidUpdate(p) {
    if (p.range !== this.props.range || p.host !== this.props.host) this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  set(patch) {
    if (this._alive) this.setState(patch);
  }
  async load() {
    const {
      range
    } = this.props;
    const prevRange = previousPreset(range);
    this.setState({
      dash: undefined,
      prev: undefined,
      series: undefined,
      prevSeries: undefined,
      channels: undefined,
      devices: undefined,
      win: undefined,
      kpis: undefined,
      pages: undefined,
      products: undefined,
      utm: undefined,
      locations: undefined,
      places: undefined,
      conversions: undefined,
      funnel: undefined
    });
    // Fire everything; each tile flips as its data lands.
    // Phase 2: the series endpoint carries the ribbon totals (range-scoped bounce, new/returning,
    // avg session) and the chart points; the dashboard stays as the fallback when it is absent.
    // The legacy dashboard call loads every impression of the range (25 s per call on LEE, and two
    // calls per view), so it is requested ONLY when the series is absent (tenants without the day rail).
    safe(getSeries(range)).then(r => {
      const series = r && r.totals ? r : null;
      this.set({
        series
      });
      if (series) {
        this.set({
          dash: null,
          prev: null
        });
        return;
      }
      safe(getDashboard(range)).then(d => this.set({
        dash: d || null
      }));
      if (prevRange) safe(getDashboard(prevRange)).then(d => this.set({
        prev: d || null
      }));else this.set({
        prev: null
      });
    });
    if (prevRange) safe(getSeries(prevRange)).then(r => this.set({
      prevSeries: r && r.totals ? r : null
    }));else this.set({
      prevSeries: null
    });
    safe(getChannels(range)).then(r => this.set({
      channels: r && r.rows ? r : null
    }));
    safe(getDevices(range)).then(r => this.set({
      devices: r && r.deviceTypes ? r : null
    }));
    safe(getWindow(range)).then(async w => {
      this.set({
        win: w || null
      });
      const k = w ? await safe(getSiteKpis(w.startDate, w.endDate)) : null;
      this.set({
        kpis: k ? siteKpiSummary(k, w.startDate, w.endDate) : null
      });
    });
    safe(getPages(range, {
      top: 8
    })).then(r => this.set({
      pages: r ? r.items : null
    }));
    safe(getProducts(range, {
      top: 6
    })).then(r => this.set({
      products: r ? r.items : null
    }));
    safe(getUtm(range)).then(r => this.set({
      utm: r ? r.items : null
    }));
    safe(getLocations(range, "city", {
      top: 60
    })).then(r => this.set({
      locations: r ? r.items : null
    }));
    safe(getPlaces(range, {
      top: 40
    })).then(r => this.set({
      places: r ? r.items : null
    }));
    safe(getConversions(range)).then(r => this.set({
      conversions: Array.isArray(r) ? r : null
    }));
    safe(getFunnels()).then(async list => {
      const rows = Array.isArray(list) ? list : list && list.items || [];
      const active = rows.find(f => f && (f.status === "active" || f.status === "Active")) || rows[0];
      if (!active) {
        this.set({
          funnel: null
        });
        return;
      }
      const res = await safe(getFunnelResults(active.id, range));
      this.set({
        funnel: res ? {
          name: active.name,
          steps: res.steps || []
        } : null
      });
    });
  }

  // ------------------------------------------------------------ ribbon
  renderRibbon() {
    const {
      dash,
      prev,
      series,
      prevSeries,
      kpis
    } = this.state;
    if (series === undefined || series === null && dash === undefined) return /*#__PURE__*/React.createElement("div", {
      className: "wa-ribbon"
    }, /*#__PURE__*/React.createElement(Pending, {
      h: 110
    }));
    const t = series && series.totals;
    const prevCoverage = prevSeries ? seriesCoverage(prevSeries) : 0;
    const prevPartial = !!(prevSeries && prevCoverage < 0.9);
    const pt = prevSeries && !prevPartial ? prevSeries.totals : null;
    const a = dash && dash.activity || {};
    const p = prev && prev.activity || null;
    if (!t && !dash) return /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, "No tracker data for this range \xB7 Manage Data \u203A Tracking");
    const rows = t ? seriesRows(series) : dailyRows(dash.performance);
    const spark = k => rows.map(r => r[k]);
    const comparing = t ? prevSeries !== undefined : prev !== undefined;
    const d = (cur, prevVal) => prevVal != null ? deltaOf(cur, prevVal) : null;
    const views = t ? t.pageViews : a.impressions,
      sessions = t ? t.sessions : a.sessions;
    const visitors = t ? t.uniqueUsers || t.users : a.users;
    const newShare = t && t.uniqueUsers > 0 ? t.newUsers / t.uniqueUsers : kpis && kpis.newShare != null ? kpis.newShare : null;
    const avgSec = t ? t.avgSessionSeconds : kpis ? kpis.avgVisitSeconds : a.avgSession;
    const bounce = t ? t.bounceRate * 100 : kpis && kpis.bounceRate != null ? Number(kpis.bounceRate) <= 1 ? Number(kpis.bounceRate) * 100 : Number(kpis.bounceRate) : null;
    const conversions = t ? t.conversions : a.conversions,
      convValue = t ? t.conversionsValue : a.conversionsValue,
      engagements = t ? t.engagements : a.engagements;
    const tiles = [{
      l: "Page views",
      v: num(views),
      d: d(views, t ? pt && pt.pageViews : p && p.impressions),
      s: rows.length ? `${num(Math.round(views / Math.max(1, rows.length)))} per ${series && series.bucket !== "day" ? series.bucket : "day"}` : null,
      sp: spark("views")
    }, {
      l: "Visitors",
      v: num(visitors),
      d: d(visitors, t ? pt && (pt.uniqueUsers || pt.users) : p && p.users),
      s: newShare != null ? `${Math.round(newShare * 100)}% new · ${Math.round((1 - newShare) * 100)}% returning` : null,
      sp: t ? spark("users") : null
    }, {
      l: "Sessions",
      v: num(sessions),
      d: d(sessions, t ? pt && pt.sessions : p && p.sessions),
      s: sessions ? `${(views / sessions).toFixed(1)} views per session` : null,
      sp: t ? spark("sessions") : null
    }, {
      l: "Avg session",
      v: avgSec != null ? fmtDuration(avgSec) : "—",
      d: t && pt ? d(avgSec, pt.avgSessionSeconds) : null,
      s: t ? "this range" : kpis && kpis.partial ? "latest full months" : kpis ? "site KPIs" : null
    }, {
      l: "Bounce rate",
      v: bounce != null ? `${bounce.toFixed(1)}%` : "—",
      d: t && pt ? d(bounce, pt.bounceRate * 100) : null,
      s: "single-view sessions",
      invert: true
    }, {
      l: "Conversions",
      v: num(conversions),
      d: d(conversions, t ? pt && pt.conversions : p && p.conversions),
      s: Number(convValue) > 0 ? `${usd(convValue)} value` : engagements ? `${num(engagements)} interactions` : null,
      sp: spark("conversions")
    }];
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-ribbon"
    }, tiles.map(tl => /*#__PURE__*/React.createElement("div", {
      className: "wa-kpi",
      key: tl.l
    }, /*#__PURE__*/React.createElement("div", {
      className: "l"
    }, tl.l), /*#__PURE__*/React.createElement("div", {
      className: "v"
    }, tl.v), /*#__PURE__*/React.createElement("div", {
      className: "row"
    }, !comparing ? /*#__PURE__*/React.createElement("span", {
      className: "wa-d flat"
    }, "\u2026") : /*#__PURE__*/React.createElement(Delta, {
      d: tl.d,
      invert: tl.invert
    })), /*#__PURE__*/React.createElement("div", {
      className: "s"
    }, tl.s || ""), tl.sp && tl.sp.length > 1 ? /*#__PURE__*/React.createElement(Sparkline, {
      points: tl.sp
    }) : /*#__PURE__*/React.createElement("div", {
      className: "wa-spark wa-spark-empty"
    }))), t && prevPartial ? /*#__PURE__*/React.createElement("div", {
      className: "wa-note"
    }, prevSeries.points && prevSeries.points.length ? /*#__PURE__*/React.createElement(React.Fragment, null, "No comparison: the tracker's history only covers ", Math.round(prevCoverage * 100), "% of the previous period (data starts ", fmtShort(prevSeries.points[0].date), ").") : /*#__PURE__*/React.createElement(React.Fragment, null, "No comparison: the tracker has no data for the previous period.")) : null, !t && kpis && kpis.sources && kpis.sources.indexOf("ga4") >= 0 ? /*#__PURE__*/React.createElement("div", {
      className: "wa-note"
    }, "Some months in this range come from GA4 history: ", kpis.note) : null);
  }

  // ------------------------------------------------------------ traffic
  renderTraffic() {
    const {
      dash,
      prev,
      series,
      prevSeries,
      metric
    } = this.state;
    const loading = series === undefined || series === null && dash === undefined;
    const body = loading ? /*#__PURE__*/React.createElement(Pending, {
      h: 240
    }) : (() => {
      const cur = series ? seriesRows(series) : dash ? dailyRows(dash.performance) : [];
      const prevRows = series ? prevSeries && seriesCoverage(prevSeries) >= 0.9 ? seriesRows(prevSeries) : null : prev ? dailyRows(prev.performance) : null;
      if (!cur.length) return /*#__PURE__*/React.createElement("div", {
        className: "wa-empty"
      }, "No daily data.");
      const k = series ? metric : "views";
      const rows = cur.map(r => ({
        date: r.date,
        value: r[k],
        marker: r.conversions
      }));
      const pv = prevRows ? prevRows.map(r => r[k]) : null;
      const aligned = pv && pv.length >= rows.length ? pv.slice(pv.length - rows.length) : pv && pv.length ? rows.map((_, i) => pv[i] == null ? 0 : pv[i]) : null;
      const label = k === "views" ? "Page views" : k === "sessions" ? "Sessions" : "Visitors";
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(AreaChart, {
        rows: rows,
        prev: aligned,
        label: label.toLowerCase()
      }), /*#__PURE__*/React.createElement("div", {
        className: "wa-legend"
      }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", {
        style: {
          background: "var(--plk-chart-current)"
        }
      }), label, ", this range"), aligned ? /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", {
        style: {
          background: "var(--plk-chart-previous)"
        }
      }), label, ", comparison") : null, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", {
        style: {
          background: "var(--plk-accent)"
        }
      }), "Conversions (markers)")));
    })();
    const bucket = series && series.bucket ? series.bucket : "day";
    return /*#__PURE__*/React.createElement(Card, {
      title: "Traffic over time",
      hint: `per ${bucket}`,
      span: 8
    }, series ? /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, [["views", "Page views"], ["sessions", "Sessions"], ["users", "Visitors"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: metric === k ? "on" : "",
      onClick: () => this.set({
        metric: k
      })
    }, l)))) : /*#__PURE__*/React.createElement("p", {
      className: "lead"
    }, "Page views per day with conversion markers."), body);
  }

  // ------------------------------------------------------------ acquisition
  renderChannels() {
    const {
      utm,
      channels
    } = this.state;
    const go = this.props.go;
    const loading = channels === undefined || channels === null && utm === undefined;
    const body = loading ? /*#__PURE__*/React.createElement(Pending, {
      h: 220
    }) : (() => {
      const list = channels ? channelsFromServer(channels) : channelsFromUtm(utm);
      if (!list.length) return /*#__PURE__*/React.createElement("div", {
        className: "wa-empty"
      }, "No sessions in this range.");
      const max = Math.max(1, ...list.map(c => c.sessions));
      const tot = list.reduce((a, c) => a + c.sessions, 0);
      return /*#__PURE__*/React.createElement("div", {
        className: "tblwrap"
      }, /*#__PURE__*/React.createElement("table", {
        className: "territory-table wa-table wa-channels compact"
      }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", null), /*#__PURE__*/React.createElement("th", {
        className: "n"
      }, "Sessions"), /*#__PURE__*/React.createElement("th", {
        className: "n"
      }, "Conv."), /*#__PURE__*/React.createElement("th", {
        className: "n"
      }, "Interactions"))), /*#__PURE__*/React.createElement("tbody", null, list.map(c => /*#__PURE__*/React.createElement("tr", {
        key: c.channel,
        className: "clickable",
        onClick: () => go("acquisition")
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, c.channel)), /*#__PURE__*/React.createElement("td", {
        className: "bar"
      }, /*#__PURE__*/React.createElement("span", {
        className: "track"
      }, /*#__PURE__*/React.createElement("i", {
        style: {
          width: `${(c.sessions / max * 100).toFixed(1)}%`
        }
      }))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.sessions), " ", /*#__PURE__*/React.createElement("small", {
        className: "dim"
      }, tot ? `${(c.sessions / tot * 100).toFixed(0)}%` : "")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.conversions)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.engagements)))))));
    })();
    return /*#__PURE__*/React.createElement(Card, {
      title: "Acquisition",
      hint: "sessions \xB7 conversions \xB7 interactions by channel",
      span: 4,
      more: "All sources \u2192",
      onMore: () => go("acquisition")
    }, body, channels && channels.truncated ? /*#__PURE__*/React.createElement("div", {
      className: "foot"
    }, "Window clamped to the last 400 days.") : null);
  }
  renderPages() {
    const {
      pages
    } = this.state;
    const go = this.props.go;
    const body = pages === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 220
    }) : !pages || !pages.length ? /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, "No page views in this range.") : (() => {
      const max = Math.max(1, ...pages.map(p => Number(p.views) || 0));
      return /*#__PURE__*/React.createElement("ul", {
        className: "wa-list"
      }, pages.slice(0, 6).map(p => /*#__PURE__*/React.createElement("li", {
        key: p.url
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        className: "t"
      }, p.title || p.url, /*#__PURE__*/React.createElement("span", {
        className: "p"
      }, pathOf(p.url))), /*#__PURE__*/React.createElement("div", {
        className: "bar"
      }, /*#__PURE__*/React.createElement("i", {
        style: {
          width: `${((Number(p.views) || 0) / max * 100).toFixed(0)}%`
        }
      }))), /*#__PURE__*/React.createElement("div", {
        className: "num"
      }, num(p.views), /*#__PURE__*/React.createElement("small", null, p.avgTimeSpanString || (p.avgTimeSpan ? fmtDuration(p.avgTimeSpan) : ""))))));
    })();
    return /*#__PURE__*/React.createElement(Card, {
      title: "Top pages",
      hint: "by views",
      span: 4,
      more: "All pages \u2192",
      onMore: () => go("pages")
    }, body);
  }
  renderProducts() {
    const {
      products
    } = this.state;
    const go = this.props.go;
    const body = products === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 220
    }) : !products || !products.length ? /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, "No product views in this range.") : /*#__PURE__*/React.createElement("ul", {
      className: "wa-list"
    }, products.map(p => /*#__PURE__*/React.createElement("li", {
      key: p.productId || p.title
    }, /*#__PURE__*/React.createElement("div", {
      className: "prow"
    }, p.imageLink ? /*#__PURE__*/React.createElement("img", {
      className: "thumb",
      src: p.imageLink,
      alt: "",
      loading: "lazy"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "thumb"
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "t"
    }, p.title), /*#__PURE__*/React.createElement("div", {
      className: "p",
      style: {
        margin: 0
      }
    }, p.productId))), /*#__PURE__*/React.createElement("div", {
      className: "num"
    }, num(p.views), /*#__PURE__*/React.createElement("small", null, num(p.conversions), " conv \xB7 ", pctOf(p.conversions, p.views).toFixed(1), "%")))));
    return /*#__PURE__*/React.createElement(Card, {
      title: "Top products",
      hint: "viewed",
      span: 4,
      more: "All products \u2192",
      onMore: () => go("products")
    }, body);
  }

  // ------------------------------------------------------------ conversions + funnel
  renderConversions() {
    const {
      conversions,
      funnel,
      series
    } = this.state;
    const totalConv = series && series.totals ? Number(series.totals.conversions) || 0 : 0;
    const list = conversions === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 120
    }) : !conversions || !conversions.length ? /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, totalConv > 0 ? `${num(totalConv)} conversions recorded · the by-name breakdown is not rolled up for this range yet.` : "No conversions in this range.") : /*#__PURE__*/React.createElement("ul", {
      className: "wa-list"
    }, conversions.slice().sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 5).map(c => /*#__PURE__*/React.createElement("li", {
      key: c.title
    }, /*#__PURE__*/React.createElement("div", {
      className: "t"
    }, c.title), /*#__PURE__*/React.createElement("div", {
      className: "num"
    }, num(c.count)))));
    const fun = funnel === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 90
    }) : !funnel || !funnel.steps.length ? null : /*#__PURE__*/React.createElement("div", {
      className: "wa-funnel"
    }, funnel.steps.map((s, i) => {
      const cur = Number(s.sessions) || 0;
      const prev = i ? Number(funnel.steps[i - 1].sessions) || 0 : 0;
      return /*#__PURE__*/React.createElement("div", {
        className: "step",
        key: i
      }, /*#__PURE__*/React.createElement("div", {
        className: "v"
      }, num(cur)), /*#__PURE__*/React.createElement("div", {
        className: "l"
      }, i + 1, " \xB7 ", s.label || s.match), i ? /*#__PURE__*/React.createElement("div", {
        className: "drop"
      }, "\u2212", prev > 0 ? Math.round((1 - cur / prev) * 100) : 0, "% from step ", i) : /*#__PURE__*/React.createElement("div", {
        className: "l"
      }, s.type === "event" ? "event" : "page · entry"));
    }));
    return /*#__PURE__*/React.createElement(Card, {
      title: "What it turned into",
      hint: "conversions",
      span: 4,
      more: funnel ? "Funnels →" : null,
      onMore: () => this.props.go("funnels")
    }, list, fun);
  }

  // ------------------------------------------------------------ geography
  renderPlaces() {
    const {
      locations,
      places,
      mapMode,
      hovered
    } = this.state;
    const go = this.props.go;
    const body = locations === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 300
    }) : (() => {
      const rows = (locations || []).filter(r => r.lat && r.lng).map(r => ({
        lat: r.lat,
        lng: r.lng,
        label: `${r.city || r.postalCode || "?"}${r.regionCode ? ", " + r.regionCode : ""}`,
        sub: r.countryCode,
        value: Number(r.sessions) || Number(r.views) || 0
      }));
      if (!rows.length && !(locations || []).length) return /*#__PURE__*/React.createElement("div", {
        className: "wa-empty"
      }, "No geo-resolved sessions in this range.");
      if (mapMode === "world") {
        // World = sessions by country on the keyless country basemap (the bubble
        // map's simplified continent outline does not line up at world scale).
        const byCountry = {};
        (locations || []).forEach(r => {
          const c = String(r.countryCode || "").toUpperCase();
          if (c.length !== 2) return;
          byCountry[c] = (byCountry[c] || 0) + (Number(r.sessions) || Number(r.views) || 0);
        });
        const countries = Object.keys(byCountry).map(code => ({
          code,
          revenue: byCountry[code],
          orders: byCountry[code]
        })).sort((a, b) => b.revenue - a.revenue);
        if (!countries.length) return /*#__PURE__*/React.createElement("div", {
          className: "wa-empty"
        }, "No sessions with a country in this range.");
        const top = countries.slice(0, 10).map(r => ({
          name: countryName(r.code),
          value: r.revenue
        }));
        return /*#__PURE__*/React.createElement("div", {
          className: "wa-map"
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(GeoChoropleth, {
          scope: "world",
          rows: countries,
          metric: "revenue",
          labels: {
            revenue: "Sessions"
          },
          height: "100%",
          onSelect: () => go("places")
        })), /*#__PURE__*/React.createElement(HBars, {
          rows: top,
          head: ["Country", "", "Sessions", ""],
          onClick: () => go("places")
        }));
      }
      const us = rows.filter(r => r.lng < -50 && r.lng > -170 && r.lat > 18 && r.lat < 72);
      if (!us.length) return /*#__PURE__*/React.createElement("div", {
        className: "wa-empty"
      }, "No US sessions in this range \xB7 switch to World.");
      const top = us.slice().sort((a, b) => b.value - a.value).slice(0, 10).map(r => ({
        name: r.label,
        value: r.value
      }));
      return /*#__PURE__*/React.createElement("div", {
        className: "wa-map"
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(PostalBubbleMap, {
        rows: us,
        hovered: hovered,
        onHover: l => this.set({
          hovered: l
        }),
        onClick: () => go("places"),
        height: 300,
        fill: true
      })), /*#__PURE__*/React.createElement(HBars, {
        rows: top,
        head: ["City", "", "Sessions", ""],
        onClick: () => go("places")
      }));
    })();
    return /*#__PURE__*/React.createElement(Card, {
      title: "Where they are",
      hint: "sessions by city",
      span: 8,
      cls: "fill",
      more: "Places \u2192",
      onMore: () => go("places")
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, /*#__PURE__*/React.createElement("button", {
      className: mapMode === "us" ? "on" : "",
      onClick: () => this.set({
        mapMode: "us"
      })
    }, "US"), /*#__PURE__*/React.createElement("button", {
      className: mapMode === "world" ? "on" : "",
      onClick: () => this.set({
        mapMode: "world"
      })
    }, "World")), places && places.length ? /*#__PURE__*/React.createElement("span", {
      className: "wa-chip"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Markets"), " ", num(places.length), " in range") : null), body);
  }
  renderDevices() {
    const {
      devices
    } = this.state;
    const body = devices === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 200
    }) : !devices || !devices.sessions ? /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, "No sessions with a device in this range.") : (() => {
      const bars = (devices.deviceTypes || []).map(r => ({
        name: r.name,
        value: r.sessions
      }));
      const unknownLast = rows => (rows || []).slice().sort((x, y) => (x.name === "Unknown") - (y.name === "Unknown") || y.sessions - x.sessions);
      const mini = (rows, label) => /*#__PURE__*/React.createElement("div", {
        style: {
          marginTop: 10
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "wa-mini-head"
      }, /*#__PURE__*/React.createElement("span", null, label), /*#__PURE__*/React.createElement("span", null, "Sessions \xB7 share")), /*#__PURE__*/React.createElement("ul", {
        className: "wa-list"
      }, unknownLast(rows).slice(0, 4).map(r => /*#__PURE__*/React.createElement("li", {
        key: r.name
      }, /*#__PURE__*/React.createElement("div", {
        className: "t"
      }, r.name), /*#__PURE__*/React.createElement("div", {
        className: "num"
      }, num(r.sessions), /*#__PURE__*/React.createElement("small", null, Math.round((Number(r.share) || 0) * 100), "%"))))));
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(HBars, {
        rows: bars,
        head: ["Device", "", "Sessions", ""]
      }), /*#__PURE__*/React.createElement("div", {
        className: "wa-2col"
      }, mini(devices.browsers, "Browser"), mini(devices.os, "OS")));
    })();
    return /*#__PURE__*/React.createElement(Card, {
      title: "Devices",
      hint: "share of sessions",
      span: 4
    }, body, devices && devices.truncated ? /*#__PURE__*/React.createElement("div", {
      className: "foot"
    }, "Window clamped to the last 400 days.") : null);
  }
  renderMovers() {
    const {
      pages,
      prev,
      dash
    } = this.state;
    return /*#__PURE__*/React.createElement(Card, {
      title: "Insights",
      hint: "from the numbers above",
      span: 12
    }, this.state.series === undefined || this.state.series === null && dash === undefined ? /*#__PURE__*/React.createElement(Pending, {
      h: 80
    }) : /*#__PURE__*/React.createElement("div", null, this.insights().map((t, i) => /*#__PURE__*/React.createElement("div", {
      className: "wa-insight",
      key: i
    }, /*#__PURE__*/React.createElement("span", {
      className: "ic"
    }, t[0]), /*#__PURE__*/React.createElement("span", null, t[1])))), pages === undefined ? null : null);
  }
  insights() {
    const {
      dash,
      prev,
      utm,
      pages,
      kpis,
      series,
      prevSeries,
      channels,
      devices
    } = this.state;
    const out = [];
    const t = series && series.totals;
    const pt = prevSeries && prevSeries.totals;
    if (!t && !dash) return out;
    const a = t ? {
      sessions: t.sessions
    } : dash.activity || {};
    const p = t ? pt && seriesCoverage(prevSeries) >= 0.9 ? {
      sessions: pt.sessions
    } : null : prev && prev.activity;
    if (p) {
      const d = deltaOf(a.sessions, p.sessions);
      if (d && d.dir !== "flat") out.push([d.dir === "up" ? "▲" : "▼", /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Sessions"), " are ", d.dir === "up" ? "up" : "down", " ", Math.abs(d.pct).toFixed(1), "% against the previous period (", num(p.sessions), " \u2192 ", num(a.sessions), ").")]);
    }
    const ch = channels ? channelsFromServer(channels) : utm && utm.length ? channelsFromUtm(utm) : [];
    if (ch.length) {
      const best = ch.slice().filter(c => c.sessions >= 20).sort((x, y) => pctOf(y.conversions, y.sessions) - pctOf(x.conversions, x.sessions))[0];
      if (best && best.conversions > 0) out.push(["◆", /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, best.channel), " converts best at ", pctOf(best.conversions, best.sessions).toFixed(1), "% of ", num(best.sessions), " sessions.")]);
      const top = ch[0];
      const tot = ch.reduce((s2, c) => s2 + c.sessions, 0);
      if (top && tot) out.push(["●", /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, top.channel), " brings ", pctOf(top.sessions, tot).toFixed(0), "% of sessions", ch.find(c => c.key === "ai") ? `; AI assistants sent ${num(ch.find(c => c.key === "ai").sessions)}` : "", ".")]);
    }
    if (devices && devices.deviceTypes && devices.deviceTypes.length) {
      const m = devices.deviceTypes.find(r => r.name === "Mobile");
      if (m && m.share >= 0.3) out.push(["●", /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, Math.round(m.share * 100), "% of sessions are on phones"), "; check the mobile heat map before changing the landing pages.")]);
    }
    if (pages && pages.length > 1) {
      const top = pages[0];
      const total = pages.reduce((s, r) => s + (Number(r.views) || 0), 0);
      out.push(["●", /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, top.title || top.url), " takes ", pctOf(top.views, total).toFixed(0), "% of the views across the top ", pages.length, " pages.")]);
    }
    const bounce = t ? t.bounceRate * 100 : kpis && kpis.bounceRate != null ? Number(kpis.bounceRate) <= 1 ? Number(kpis.bounceRate) * 100 : Number(kpis.bounceRate) : null;
    if (bounce != null) out.push([bounce > 55 ? "▼" : "●", /*#__PURE__*/React.createElement("span", null, "Bounce rate is ", bounce.toFixed(1), "%", bounce > 55 ? ", above the 55% line where landing pages usually need work" : ", inside the healthy band", ".")]);
    if (!out.length) out.push(["●", /*#__PURE__*/React.createElement("span", null, "Not enough activity in this range for a read.")]);
    return out;
  }
  render() {
    return /*#__PURE__*/React.createElement("div", null, this.renderRibbon(), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, this.renderTraffic(), this.renderChannels(), this.renderPages(), this.renderProducts(), this.renderConversions(), this.renderPlaces(), this.renderDevices(), this.renderMovers()));
  }
}
const REGION_NAMES = (() => {
  try {
    return new Intl.DisplayNames(["en"], {
      type: "region"
    });
  } catch (e) {
    return null;
  }
})();
function countryName(code) {
  try {
    return REGION_NAMES && REGION_NAMES.of(code) || code;
  } catch (e) {
    return code;
  }
}
function fmtShort(v) {
  const d = new Date(v);
  return isNaN(d) ? "" : d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}
export function pathOf(url) {
  try {
    const u = new URL(url);
    return u.pathname + (u.search || "");
  } catch (e) {
    return url;
  }
}